import type { QueueMatch } from "../../lib/api";
import { estimateFeePaise, estimateSharePaise, formatCities, formatExperience, formatPaise, formatPaiseRange } from "../../lib/format";
import type { Job } from "../../types/api";
import { daysSince, EXPLORE_WORK_MODE_OPTIONS, SOURCING_ORIGIN_COPY, type SourcingOrigin } from "./constants";

export function sourcingOrigin(match: QueueMatch): SourcingOrigin {
  if (match.inbound_for_job) return "jd_link";
  const source = match.candidate?.source;
  const ownership = match.candidate?.ownership;
  if (ownership === "recruiter" || source === "recruiter_link" || source === "recruiter_pool") {
    return "recruiter_link";
  }
  return "axiohire";
}

export function youEarnLabel(job: Job): string {
  const fee = estimateFeePaise(job);
  const recruiterShare = estimateSharePaise(fee, job.recruiter_fee_pct);
  const sourcingShare = estimateSharePaise(fee, job.sourcing_fee_pct);
  if (!recruiterShare && !sourcingShare) return "—";
  const combined = {
    min: (recruiterShare?.min ?? 0) + (sourcingShare?.min ?? 0),
    max: (recruiterShare?.max ?? 0) + (sourcingShare?.max ?? 0),
  };
  return formatPaiseRange(combined.min, combined.max);
}

export function updatedAgoLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const days = daysSince(iso);
  if (days <= 0) return "Updated today";
  if (days === 1) return "Updated 1d ago";
  return `Updated ${days}d ago`;
}

export function candidateNotice(c: NonNullable<QueueMatch["candidate"]>): string | null {
  if (c.availability_in_days == null) return null;
  if (c.availability_in_days === 0) return "Immediate";
  return `${c.availability_in_days}d notice`;
}

export function candidateMetaLine(c: NonNullable<QueueMatch["candidate"]>): string {
  return [
    formatCities(c.cities),
    c.years_experience != null ? `${formatExperience(c.years_experience)} Exp` : null,
    candidateNotice(c),
  ].filter(Boolean).join(" · ");
}

export function workModeLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return EXPLORE_WORK_MODE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export type CandidateFact = { label: string; value: string };

export function candidateFacts(c: NonNullable<QueueMatch["candidate"]>): CandidateFact[] {
  const facts: CandidateFact[] = [];
  if (c.current_company) facts.push({ label: "Company", value: c.current_company });
  if (c.years_experience != null) facts.push({ label: "Experience", value: formatExperience(c.years_experience) });
  const notice = candidateNotice(c);
  if (notice) facts.push({ label: "Notice", value: notice });
  const mode = workModeLabel(c.work_mode_preference);
  if (mode) facts.push({ label: "Work mode", value: mode });
  if (c.cities.length > 0) facts.push({ label: "Cities", value: formatCities(c.cities) });
  if (c.willing_to_relocate) facts.push({ label: "Relocate", value: "Open to relocate" });
  if (c.expected_ctc_paise != null) facts.push({ label: "Expected CTC", value: formatPaise(c.expected_ctc_paise) });
  if (c.employment_status) {
    facts.push({ label: "Status", value: c.employment_status.replaceAll("_", " ") });
  }
  if (c.education_level) facts.push({ label: "Education", value: c.education_level.replaceAll("_", " ") });
  if (c.languages && c.languages.length > 0) facts.push({ label: "Languages", value: c.languages.join(", ") });
  return facts;
}

export function exploreQueueEmptyCopy(mustHaveOnly: boolean, callableOnly: boolean, savedCount = 0) {
  if (savedCount > 0) {
    return {
      emptyTitle: "Call someone you saved",
      emptyDescription: "Call or Pass a saved candidate to see the next person in this queue.",
    };
  }
  const emptyTitle = mustHaveOnly
    ? "No candidates match must-have skills"
    : callableOnly
      ? "No callable profiles in this queue"
      : "Queue is empty";
  const emptyDescription = mustHaveOnly
    ? "Uncheck “Must-have skills only” to see stretch fits."
    : callableOnly
      ? "Uncheck “Callable profiles only” to see incomplete rows."
      : "No available candidates for this job right now.";
  return { emptyTitle, emptyDescription };
}

export function sourcingCopyFor(match: QueueMatch) {
  return SOURCING_ORIGIN_COPY[sourcingOrigin(match)];
}

export function saveForwardLabel(savedCount: number, savedLimit: number) {
  return `Save forward (${savedCount}/${savedLimit})`;
}
